/**
 * LeafletMap: the imperative Leaflet surface under the capacity map and
 * the resource directory's map view (one map component, two homes).
 * The layer props carry the capacity map's names: on the directory map
 * the provider organizations ride `cmPins` (the pin-card layer: hover
 * and tap callbacks, an aria-label instead of a tooltip), "my clients"
 * rides `clientPins`, and the client the search chose is the
 * `placementPin`.
 *
 * Tiles come through Olive (/v1/map/tiles/{z}/{x}/{y}), so the Azure
 * Maps key never reaches the browser. A tile the relay can't fetch gets
 * a transparent errorTileUrl (TRANSPARENT_TILE_PNG below) instead of
 * Leaflet's default grey error box, so a failure reads as bare paper
 * background rather than a visible rectangle. Layer groups (care
 * managers, clients, lines, prospects, the "place a client" pin) are
 * added or removed as the toggles change; markers are circle markers (no
 * image assets), coloured by the caller.
 *
 * THE FIRST FRAME. The map fits `homeBounds` once, when it is created,
 * and never again: the caller passes the tenant's working-area box
 * (getTenantConfig's `working_area`, the whole country when that read
 * fails), and nothing a layer draws, however far away, widens or moves
 * it. The box's minimum span bounds how far in it opens.
 *
 * THE ONE MOVE PATH. After the first frame the map moves only when the
 * person asks: the caller hands in a `focusRequest` (a point and a key)
 * when a search choice, a placement pin or the client a URL names asks
 * for it. A request whose key differs from the last one applied moves the
 * map once, and only when its point lies inside `workingArea`; otherwise
 * the map stays where it is (the caller says so). The same key on any
 * number of re-renders moves nothing; a new key for the same point moves
 * the map again, because the person asked again. The placement pin only
 * draws, and redraws only when its point or colour changes. Leaflet
 * ignores a new view set while a zoom animation is still running, and the
 * map then settles where that animation was headed, so a request that
 * lands during one waits for it to end (zoomanim and zoomend) and is set
 * then; a newer request arriving meanwhile replaces it. The canvas changes
 * width when a panel opens beside it, so the map is told its new size
 * whenever the host resizes (Leaflet keeps the centre and the zoom), and a
 * move centres the point in the canvas as it now is.
 *
 * Distance rays (the "lines to clients" layer) are drawn rim-to-rim, not
 * center-to-center: each endpoint is pulled back by the pin it touches —
 * MapLine.fromRadius / toRadius, plus RAY_RIM_GAP_PX — using
 * trimSegmentPx (src/lib/geo.ts) on the two endpoints' screen-pixel
 * projections (map.latLngToLayerPoint), then unprojected back. Because
 * the trim is a pixel quantity but the ray is stored as lat/lng, it's
 * redrawn (not just re-rendered) on every zoomend/moveend so the gap
 * stays the same number of pixels at every zoom.
 *
 * The base map is a paper-quiet, near-monochrome treatment client-side
 * (the `map-tile-muted` class, src/index.css) so the marker layer — not
 * the roads underneath it — is the brightest thing on the screen. This is
 * belt-and-braces alongside the tileset the tile proxy requests
 * (api/_shared/geocode/azureMapsTiles.ts): Azure Maps Render V2 has no
 * built-in muted/grayscale variant of the light road tileset (that
 * naming belongs to the deprecated Static Image API's `style` param), so
 * the CSS filter here is the actual mechanism, not a backup for one.
 *
 * Two interaction modes ride the same marker plumbing as the base
 * hover/click behavior:
 *   - the care-manager hover/tap card: onCmPinHover fires on mouseover
 *     (desktop) with the pin's container-relative pixel point so the
 *     caller can anchor a card beside it, and with null on mouseout;
 *     onCmPinTap fires on click/tap so a touch caller can show it
 *     without a hover event, and toggle it off on a second tap. A
 *     care-manager pin carries no Leaflet tooltip of its own — the card
 *     already says everything it would — just an aria-label (fillLayer's
 *     skipTooltip), so the pin still has an accessible name.
 *   - "place a client"'s existing-client pick: while pickClientMode is
 *     on, clicking a CLIENT pin reports the pick (onPickClientPin)
 *     INSTEAD of navigating to the record — the form owns what happens
 *     next, not the map.
 *
 * A third ride, also on the same rim-to-rim ray drawing: `selectionLine`
 * is "place a client"'s own single ray, from the placement pin to a
 * care manager the caller (CapacityMapPanel) has selected — same
 * trimSegmentPx math as the "lines to clients" layer, but its own
 * always-attached layer (no showClients/showLines gating; a placement
 * selection is its own workflow) so the two rays never fight over one
 * toggle. When the line carries a `label`, it's bound as a permanent,
 * centered Leaflet tooltip (`.map-distance-chip`, src/index.css) riding
 * the ray's midpoint — the same distance text the ranked list shows for
 * that care manager, not a second computation of it.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { DISTANCE_RAY_OPACITY, DISTANCE_RAY_WEIGHT } from '@/lib/mapColors';
import { trimSegmentPx } from '@/lib/geo';
import { insideBounds, type MapBounds } from '@/lib/mapView';

/** A fully transparent 1x1 PNG, used as the tile layer's errorTileUrl —
 *  when a tile request fails (Azure 429/5xx, or the relay's own
 *  timeout), Leaflet paints this instead of its default grey error box,
 *  so a failed tile reads as the paper background underneath rather than
 *  a visible flat-grey rectangle. */
export const TRANSPARENT_TILE_PNG =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNk+A8AAQUBAScY42YAAAAASUVORK5CYII=';

/** The canvas height when the caller names none (a panel-sized map). */
export const DEFAULT_MAP_HEIGHT_CLASS = 'h-[28rem]';

/** How close a focus request zooms: at least this, closer if already. */
export const PLACEMENT_ZOOM = 12;

/** A request to move the map to a point, made when the person asks. A
 *  new `key` is a new ask; the same key again is not. */
export interface FocusRequest {
  lat: number;
  lng: number;
  key: number;
}

/** The caller's side of the move path: the current request, and `ask`,
 *  which issues a new one for a point (a new key every call). */
export function useFocusRequest(): [FocusRequest | null, (point: { lat: number; lng: number }) => void] {
  const [request, setRequest] = useState<FocusRequest | null>(null);
  const ask = useCallback((point: { lat: number; lng: number }): void => {
    setRequest((prev) => ({ lat: point.lat, lng: point.lng, key: (prev?.key ?? 0) + 1 }));
  }, []);
  return [request, ask];
}

export interface MapPin {
  id: string;
  lat: number;
  lng: number;
  color: string;
  /** Hover text for a client/prospect pin. A care-manager pin has no
   *  tooltip (its hover card says this instead); fillLayer sets this
   *  string as that pin's aria-label so it keeps an accessible name. */
  title: string;
  /** Opens when the pin is clicked; null = no navigation. (Ignored when
   *  a handlers.onClick override is wired for this pin's layer — see
   *  pickClientMode.) */
  href: string | null;
  radius?: number;
  /** Stroke weight; defaults to the shared BASE_WEIGHT so every marker
   *  matches unless the caller means to draw attention (a ranked "place
   *  a client" result highlighted on the map). */
  weight?: number;
  /** false = a hollow ring in `color` (the prospect marker class)
   *  instead of a solid fill with a white stroke. Defaults to true. */
  filled?: boolean;
  /** Overrides the default stroke with a colored ring: the care manager's
   *  unit color (see CapacityMapPanel), whatever the fill is. When set it
   *  is the stroke whether or not the pin is `filled`. */
  ringColor?: string;
  /** Stroke weight while hovered; defaults to the shared HOVER_WEIGHT. */
  hoverWeight?: number;
  /** While true, this pin draws at DIMMED_PIN_OPACITY instead of full
   *  opacity (both fill and stroke) — "place a client"'s selected-care-
   *  manager view, so a client belonging to someone else recedes
   *  without disappearing (CapacityMapPanel keeps every client pin on
   *  the map; this is the only thing that changes). */
  dimmed?: boolean;
  /** LP-43: a name tag beside the pin (a permanent tooltip, NAME_TAG
   *  below) in place of the hover title, for the client the search chose.
   *  The title stays the pin's accessible name; a click still opens href. */
  tag?: string;
  /** The pin's resting opacity (fill and stroke), 0..1; defaults to 1.
   *  Client pins draw at CLIENT_PIN_OPACITY so the care-manager pins and
   *  the base map read through a dense caseload. `dimmed` still wins:
   *  a dimmed pin is at DIMMED_PIN_OPACITY whatever this says. */
  opacity?: number;
}

export interface MapLine {
  from: [number, number];
  to: [number, number];
  color: string;
  /** Radius (px) of the pin drawn at `from` — the ray is trimmed back to
   *  that pin's rim (radius + RAY_RIM_GAP_PX) rather than its center. */
  fromRadius: number;
  /** Radius (px) of the pin drawn at `to`, trimmed the same way. */
  toRadius: number;
  /** Bound as a permanent, centered tooltip at the ray's midpoint (a
   *  "paper chip", .map-distance-chip) when set — "place a client"'s
   *  selection ray uses this for the distance text; the plain "lines to
   *  clients" rays leave it unset and draw no label. */
  label?: string;
}

export interface PlacementPin {
  lat: number;
  lng: number;
  color: string;
  client?: { name: string; href: string };
}

export interface LeafletMapProps {
  tileUrl: string;
  cmPins: MapPin[];
  clientPins: MapPin[];
  lines: MapLine[];
  showCms: boolean;
  showClients: boolean;
  onOpen: (href: string) => void;
  /** Height classes for the canvas; the default is a panel-sized map. */
  heightClass?: string;
  /** Third marker class: open intake prospects with coordinates, shown
   *  and hidden exactly like clientPins (whenever the Clients layer is
   *  on). Render as hollow rings by passing filled: false on each pin. */
  prospectPins?: MapPin[];
  /** The placed point: "place a client"'s in-progress pin, or the
   *  directory's chosen client; null = nothing placed. The map moves only
   *  on a `focusRequest`. LP-43: given `client`, the pin carries the
   *  client's name tag and a click (on the pin or the tag) opens `href`;
   *  without it (the in-progress pin, which has no client) the pin only
   *  draws. */
  placementPin?: PlacementPin | null;
  /** The person's latest ask to move the map (see THE ONE MOVE PATH). */
  focusRequest?: FocusRequest | null;
  /** "Place a client"'s single ray to the selected care manager; null =
   *  no selection. Its own always-attached layer, drawn/redrawn the same
   *  rim-to-rim way as `lines` (see the header doc) but independent of
   *  showClients/showLines. */
  selectionLine?: MapLine | null;
  /** Hover (desktop) on a CM pin — the pin's container-relative pixel
   *  point for the card's anchor; null on mouseout. */
  onCmPinHover?: (rosterId: string | null, point: { x: number; y: number } | null) => void;
  /** Click/tap on a CM pin — the caller owns the show/toggle/hide logic. */
  onCmPinTap?: (rosterId: string, point: { x: number; y: number }) => void;
  /** LP-43: the placed client's pin behaves as the
   *  Directory's other pins do. Hover (desktop) reports the pin's
   *  container-relative point for a card's anchor, null on mouseout; a
   *  click or tap reports the point and the caller shows or hides the
   *  card. Given `onPlacementPinTap`, a click no longer opens `href`. */
  onPlacementPinHover?: (point: { x: number; y: number } | null) => void;
  onPlacementPinTap?: (point: { x: number; y: number }) => void;
  /** While true, clicking a CLIENT pin reports the pick instead of
   *  navigating to the record. */
  pickClientMode?: boolean;
  onPickClientPin?: (clientId: string, lat: number, lng: number) => void;
  /** The canvas element's test id; each home names its own. */
  canvasTestId?: string;
  /** The view the map opens on, fitted once when the map is created. */
  homeBounds: MapBounds;
  /** The tenant's working area: a focus request inside it moves the map;
   *  one outside it does not. */
  workingArea: MapBounds;
}

const ATTRIBUTION = 'Map data: Microsoft Azure Maps, TomTom';

export function LeafletMap({
  tileUrl,
  cmPins,
  clientPins,
  lines,
  showCms,
  showClients,
  onOpen,
  heightClass = DEFAULT_MAP_HEIGHT_CLASS,
  prospectPins = [],
  placementPin = null,
  focusRequest = null,
  selectionLine = null,
  onCmPinHover,
  onCmPinTap,
  onPlacementPinHover,
  onPlacementPinTap,
  pickClientMode = false,
  onPickClientPin,
  canvasTestId = 'capacity-map-canvas',
  homeBounds,
  workingArea,
}: LeafletMapProps): ReactNode {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const cmLayerRef = useRef<L.LayerGroup | null>(null);
  const clientLayerRef = useRef<L.LayerGroup | null>(null);
  const lineLayerRef = useRef<L.LayerGroup | null>(null);
  const prospectLayerRef = useRef<L.LayerGroup | null>(null);
  const placementLayerRef = useRef<L.LayerGroup | null>(null);
  const selectionLineLayerRef = useRef<L.LayerGroup | null>(null);
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;
  const onCmPinHoverRef = useRef(onCmPinHover);
  onCmPinHoverRef.current = onCmPinHover;
  const onCmPinTapRef = useRef(onCmPinTap);
  onCmPinTapRef.current = onCmPinTap;
  const onPickClientPinRef = useRef(onPickClientPin);
  onPickClientPinRef.current = onPickClientPin;
  const onPlacementPinHoverRef = useRef(onPlacementPinHover);
  onPlacementPinHoverRef.current = onPlacementPinHover;
  const onPlacementPinTapRef = useRef(onPlacementPinTap);
  onPlacementPinTapRef.current = onPlacementPinTap;
  // Read when the map is created and when a request is applied, never as
  // a reason to refit: a new box object is not a new first frame.
  const homeBoundsRef = useRef(homeBounds);
  homeBoundsRef.current = homeBounds;
  const workingAreaRef = useRef(workingArea);
  workingAreaRef.current = workingArea;
  // True while a zoom animation runs (between zoomanim and zoomend).
  const zoomAnimatingRef = useRef(false);
  // The latest request, read by the move effect, which runs on a new key.
  const focusRequestRef = useRef(focusRequest);
  focusRequestRef.current = focusRequest;
  const appliedFocusKeyRef = useRef<number | null>(null);

  useEffect(() => {
    if (!hostRef.current || mapRef.current) return;
    const map = L.map(hostRef.current, { scrollWheelZoom: false });
    map.on('zoomanim', () => {
      zoomAnimatingRef.current = true;
    });
    map.on('zoomend', () => {
      zoomAnimatingRef.current = false;
    });
    const home = homeBoundsRef.current;
    map.fitBounds([
      [home.south, home.west],
      [home.north, home.east],
    ]);
    L.tileLayer(tileUrl, {
      maxZoom: 19,
      attribution: ATTRIBUTION,
      className: 'map-tile-muted',
      errorTileUrl: TRANSPARENT_TILE_PNG,
    }).addTo(map);
    mapRef.current = map;
    // LP-43: the placed client draws in a pane of her
    // own above the overlay pane, so the facility she lives in never
    // covers her pin, whatever order the layers were added in.
    map.createPane(PLACEMENT_PANE).style.zIndex = String(PLACEMENT_PANE_Z);
    cmLayerRef.current = L.layerGroup();
    clientLayerRef.current = L.layerGroup();
    lineLayerRef.current = L.layerGroup();
    prospectLayerRef.current = L.layerGroup();
    // The placement pin has no toggle of its own — always attached,
    // empty (so it draws nothing) until a pin is set.
    placementLayerRef.current = L.layerGroup().addTo(map);
    // Same story for the selection ray: no toggle, always attached,
    // empty until "place a client" has both a pin and a picked care
    // manager.
    selectionLineLayerRef.current = L.layerGroup().addTo(map);
    // LP-43: a name tag's side (placeNameTag) is chosen when it opens and
    // again whenever a move or a zoom ends, so a pan that carries its pin
    // toward an edge never leaves the tag hanging off the map.
    const placeTags = (): void => {
      for (const group of [clientLayerRef.current, placementLayerRef.current]) {
        group?.eachLayer((layer) => {
          if (layer.getTooltip()?.options.permanent) placeNameTag(layer as L.CircleMarker);
        });
      }
    };
    map.on('moveend', placeTags);
    map.on('zoomend', placeTags);
    // The canvas narrows when a panel opens beside it; the map is told
    // its new size so a later move centres in the canvas as it is.
    const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => map.invalidateSize());
    resize?.observe(hostRef.current);
    return () => {
      resize?.disconnect();
      map.remove();
      // The applied key and the running zoom belong to the map just
      // removed: a map made on a remount has applied nothing yet, so a
      // request already present moves it.
      appliedFocusKeyRef.current = null;
      zoomAnimatingRef.current = false;
      mapRef.current = null;
      cmLayerRef.current = null;
      clientLayerRef.current = null;
      lineLayerRef.current = null;
      prospectLayerRef.current = null;
      placementLayerRef.current = null;
      selectionLineLayerRef.current = null;
    };
  }, [tileUrl]);

  useEffect(() => {
    const layer = cmLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    for (const pin of cmPins) {
      fillLayer(
        layer,
        pin,
        CM_PIN_RADIUS,
        onOpenRef,
        {
          onHover: (p, point) => onCmPinHoverRef.current?.(p.id, point),
          onHoverEnd: () => onCmPinHoverRef.current?.(null, null),
          onClick: (p, point) => onCmPinTapRef.current?.(p.id, point),
        },
        { skipTooltip: true },
      );
    }
  }, [cmPins]);

  useEffect(() => {
    const layer = clientLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    for (const pin of clientPins) {
      fillLayer(
        layer,
        pin,
        CLIENT_PIN_RADIUS,
        onOpenRef,
        pickClientMode ? { onClick: (p) => onPickClientPinRef.current?.(p.id, p.lat, p.lng) } : undefined,
      );
    }
  }, [clientPins, pickClientMode]);

  useEffect(() => {
    const layer = prospectLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    for (const pin of prospectPins) fillLayer(layer, pin, CLIENT_PIN_RADIUS, onOpenRef);
  }, [prospectPins]);

  // Trimmed in screen pixels, not lat/lng: a ray's endpoints sit on the
  // two pins' RIMS, not their centers, so it's redrawn (not just
  // re-projected) on every zoomend/moveend — the same lat/lng gap would
  // look like a different pixel gap at a different zoom. Shared by the
  // "lines to clients" layer and the "place a client" selection ray
  // below — same trim math, same polyline styling, two independent
  // layers so one's toggle never hides the other's.
  useEffect(() => {
    const layer = lineLayerRef.current;
    const map = mapRef.current;
    if (!layer || !map) return;

    const draw = (): void => {
      layer.clearLayers();
      for (const line of lines) drawTrimmedLine(map, layer, line);
    };

    draw();
    map.on('zoomend', draw);
    map.on('moveend', draw);
    return () => {
      map.off('zoomend', draw);
      map.off('moveend', draw);
    };
  }, [lines]);

  useEffect(() => {
    const layer = selectionLineLayerRef.current;
    const map = mapRef.current;
    if (!layer || !map) return;

    const draw = (): void => {
      layer.clearLayers();
      if (selectionLine) drawTrimmedLine(map, layer, selectionLine);
    };

    draw();
    map.on('zoomend', draw);
    map.on('moveend', draw);
    return () => {
      map.off('zoomend', draw);
      map.off('moveend', draw);
    };
  }, [selectionLine]);

  // The placed point redraws only when its point, colour or client
  // changes: a new object holding the same values draws nothing. A
  // client's pin carries the name tag and opens the record; the
  // in-progress pin has neither, and takes no clicks.
  const placementLat = placementPin?.lat ?? null;
  const placementLng = placementPin?.lng ?? null;
  const placementColor = placementPin?.color ?? null;
  const placementName = placementPin?.client?.name ?? null;
  const placementHref = placementPin?.client?.href ?? null;
  useEffect(() => {
    const layer = placementLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    if (placementLat === null || placementLng === null || placementColor === null) return;
    const opensRecord = placementName !== null && placementHref !== null;
    const marker = L.circleMarker([placementLat, placementLng], {
      radius: PLACEMENT_PIN_RADIUS,
      color: '#FFFFFF',
      weight: PLACEMENT_PIN_STROKE_WEIGHT,
      fillColor: placementColor,
      fillOpacity: 1,
      className: 'map-marker-pin',
      interactive: opensRecord,
      pane: PLACEMENT_PANE,
    });
    if (opensRecord) {
      marker.bindTooltip(placementName, nameTagOptions(PLACEMENT_PIN_RADIUS));
      marker.on('tooltipopen', () => placeNameTag(marker));
      marker.on('add', () => marker.getElement()?.setAttribute('aria-label', placementName));
      // As the other pins: it grows under the pointer, and the caller
      // anchors a card to the point it reports. A click on the name tag
      // reaches the marker too (the tag is interactive).
      marker.on('mouseover', (e?: L.LeafletMouseEvent) => {
        marker.setRadius(PLACEMENT_PIN_RADIUS + HOVER_RADIUS_DELTA);
        if (e?.containerPoint) onPlacementPinHoverRef.current?.({ x: e.containerPoint.x, y: e.containerPoint.y });
      });
      marker.on('mouseout', () => {
        marker.setRadius(PLACEMENT_PIN_RADIUS);
        onPlacementPinHoverRef.current?.(null);
      });
      marker.on('click', (e?: L.LeafletMouseEvent) => {
        const tap = onPlacementPinTapRef.current;
        if (tap) tap(e?.containerPoint ? { x: e.containerPoint.x, y: e.containerPoint.y } : { x: 0, y: 0 });
        else onOpenRef.current(placementHref);
      });
    }
    marker.addTo(layer);
  }, [placementLat, placementLng, placementColor, placementName, placementHref]);

  // The one move path: a request with a key not yet applied moves the map
  // once, inside the working area only.
  const focusKey = focusRequest?.key ?? null;
  useEffect(() => {
    const map = mapRef.current;
    const request = focusRequestRef.current;
    if (!map || !request || request.key === appliedFocusKeyRef.current) return;
    appliedFocusKeyRef.current = request.key;
    if (!insideBounds(request, workingAreaRef.current)) return;
    const target: [number, number] = [request.lat, request.lng];
    const move = (): void => {
      map.setView(target, Math.max(map.getZoom(), PLACEMENT_ZOOM));
    };
    if (!zoomAnimatingRef.current) {
      move();
      return;
    }
    // A zoom animation is running and would drop the view: move when it
    // ends. A newer request runs this effect again, and the cleanup drops
    // this one, so only the newest lands.
    const afterZoom = (): void => {
      map.off('zoomend', afterZoom);
      move();
    };
    map.on('zoomend', afterZoom);
    return () => {
      map.off('zoomend', afterZoom);
    };
  }, [focusKey]);

  useEffect(() => {
    const map = mapRef.current;
    const layer = cmLayerRef.current;
    if (!map || !layer) return;
    if (showCms) layer.addTo(map);
    else layer.remove();
  }, [showCms, cmPins]);

  useEffect(() => {
    const map = mapRef.current;
    const layer = clientLayerRef.current;
    const lineLayer = lineLayerRef.current;
    const prospectLayer = prospectLayerRef.current;
    if (!map || !layer || !lineLayer || !prospectLayer) return;
    if (showClients) {
      layer.addTo(map);
      lineLayer.addTo(map);
      prospectLayer.addTo(map);
    } else {
      layer.remove();
      lineLayer.remove();
      prospectLayer.remove();
    }
  }, [showClients, clientPins, lines, prospectPins]);

  // The host isolates Leaflet's own stacking (panes at 400, controls at
  // 1000) so a sibling panel or card beside the map sits above it.
  return <div ref={hostRef} data-testid={canvasTestId} className={`${heightClass} relative isolate z-0 w-full rounded-card overflow-hidden bg-sand`} />;
}

/** Stroke on an unselected/unhovered marker. Hover (or a "highlighted"
 *  pin, via MapPin.weight) goes to HOVER_WEIGHT with radius bumped by
 *  HOVER_RADIUS_DELTA — the same rule for every pin, so a hovered client
 *  dot and a hovered CM pin lift off the map the same way. */
export const BASE_WEIGHT = 2;
export const HOVER_WEIGHT = 3;
export const HOVER_RADIUS_DELTA = 2;
/** Care manager pins are the largest mark; client and prospect pins a
 *  step smaller but still readable at the default zoom. */
export const CM_PIN_RADIUS = 8;
export const CLIENT_PIN_RADIUS = 7.5;

/** A pin drawn at MapPin.dimmed — "place a client"'s selected-care-
 *  manager view fades every OTHER client pin to this (both fill and
 *  stroke) rather than removing it, so the map still shows where they
 *  are, just recessive next to the selected caseload. */
export const DIMMED_PIN_OPACITY = 0.35;
/** A client pin's resting opacity: 40% see-through, so a crowded caseload
 *  never hides the care-manager pins or the streets under it, while the
 *  hue still reads as the client colour. */
export const CLIENT_PIN_OPACITY = 0.6;

/** Extra clearance a distance ray keeps beyond the pin's own radius, so
 *  it stops just short of the rim rather than touching it. */
export const RAY_RIM_GAP_PX = 1;

/** The "place a client" pin's own radius — the ray to a selected care
 *  manager (CapacityMapPanel's selectionLine) trims back from this end
 *  the same way it trims from CM_PIN_RADIUS at the other. */
export const PLACEMENT_PIN_RADIUS = 10;

/** The "place a client" pin's white stroke — thicker than every other
 *  marker's (BASE_WEIGHT) so the pin the panel is actively locating is
 *  unmistakably its own thing, on top of the darkest fill on the map
 *  (PLACEMENT_PIN_COLOR, src/lib/mapColors.ts). */
export const PLACEMENT_PIN_STROKE_WEIGHT = 3;

/** LP-43: the chosen client's name tag, a permanent Leaflet tooltip
 *  beside the pin (.map-name-tag, src/index.css: the legend's text on a
 *  small paper label). It opens on the right; placeNameTag moves it to
 *  the side with room. Interactive, so a click on the tag reaches the pin
 *  (a tooltip's events bubble to its source) and opens the record. */
export function nameTagOptions(pinRadius: number): L.TooltipOptions {
  return {
    permanent: true,
    direction: 'right',
    offset: [pinRadius + NAME_TAG_GAP_PX, 0],
    className: 'map-name-tag',
    interactive: true,
  };
}

/** The room a name tag keeps from the map's edge. */
export const NAME_TAG_EDGE_PX = 4;

/** Where a name tag goes beside its pin: the right when the whole tag fits
 *  there, else the left when it fits there, else the side with more room,
 *  narrowed to that room so its ellipsis takes the rest and nothing hangs
 *  off the map (maxWidth null = no narrowing). Pixels across the map. */
export function nameTagLayout(
  pinX: number,
  mapWidth: number,
  tagWidth: number,
  pinRadius: number,
): { side: 'right' | 'left'; maxWidth: number | null } {
  const reach = pinRadius + NAME_TAG_GAP_PX + NAME_TAG_EDGE_PX;
  const roomRight = mapWidth - pinX - reach;
  const roomLeft = pinX - reach;
  if (roomRight >= tagWidth) return { side: 'right', maxWidth: null };
  if (roomLeft >= tagWidth) return { side: 'left', maxWidth: null };
  return roomRight >= roomLeft
    ? { side: 'right', maxWidth: Math.max(roomRight, 0) }
    : { side: 'left', maxWidth: Math.max(roomLeft, 0) };
}

/** Lays a tag out beside its pin (nameTagLayout), measured from the page
 *  (the pin's and the map's boxes, the tag at its natural width), then
 *  repositions it. Leaflet's own 'auto' only asks which half of the map
 *  the pin is in, which leaves a long name past the edge of a narrow map. */
export function placeNameTag(marker: L.CircleMarker): void {
  const tag = marker.getTooltip();
  if (!tag) return;
  const el = tag.getElement();
  if (el) {
    const box = el.closest('.leaflet-container')?.getBoundingClientRect();
    const pin = marker.getElement()?.getBoundingClientRect();
    if (box && pin) {
      el.style.maxWidth = '';
      const radius = marker.getRadius();
      const { side, maxWidth } = nameTagLayout((pin.left + pin.right) / 2 - box.left, box.width, el.offsetWidth, radius);
      const gap = radius + NAME_TAG_GAP_PX;
      tag.options.direction = side;
      tag.options.offset = [side === 'right' ? gap : -gap, 0];
      if (maxWidth !== null) el.style.maxWidth = `${maxWidth}px`;
    }
  }
  tag.update();
}

/** The placed client's own pane (LP-43): above Leaflet's overlay pane
 *  (400), where every other pin draws, and below the shadow pane (500). */
export const PLACEMENT_PANE = 'placement';
export const PLACEMENT_PANE_Z = 450;

/** The gap between a pin's rim and its name tag (the tag's CSS zeroes
 *  Leaflet's own 6px arrow nudge, so this is the whole gap). */
export const NAME_TAG_GAP_PX = 6;

/** Draws one trimmed-to-the-rim ray (see the header doc) into `layer`,
 *  including its optional midpoint distance chip — shared by the "lines
 *  to clients" layer and "place a client"'s own selection ray, so both
 *  reuse the exact same trim math and polyline styling. */
function drawTrimmedLine(map: L.Map, layer: L.LayerGroup, line: MapLine): void {
  const fromPoint = map.latLngToLayerPoint(line.from);
  const toPoint = map.latLngToLayerPoint(line.to);
  const trimmed = trimSegmentPx(fromPoint, toPoint, line.fromRadius + RAY_RIM_GAP_PX, line.toRadius + RAY_RIM_GAP_PX);
  if (!trimmed) return;
  const from = map.layerPointToLatLng([trimmed.a.x, trimmed.a.y]);
  const to = map.layerPointToLatLng([trimmed.b.x, trimmed.b.y]);
  const polyline = L.polyline([from, to], { color: line.color, weight: DISTANCE_RAY_WEIGHT, opacity: DISTANCE_RAY_OPACITY });
  polyline.addTo(layer);
  if (line.label) polyline.bindTooltip(line.label, { permanent: true, direction: 'center', className: 'map-distance-chip' });
}

interface PinHandlers {
  onHover?: (pin: MapPin, point: { x: number; y: number }) => void;
  onHoverEnd?: () => void;
  /** Overrides the default href-navigation click behavior when set. */
  onClick?: (pin: MapPin, point: { x: number; y: number }) => void;
}

interface FillLayerOptions {
  /** True for care-manager pins: a hover card already says everything
   *  the Leaflet tooltip would, so binding both would say it twice.
   *  The pin still needs an accessible name, so this sets pin.title as
   *  the marker's aria-label instead — on the 'add' event, not right
   *  after addTo(), because a marker added to a LayerGroup that isn't
   *  on the map yet has no rendered element until the group itself is
   *  mounted (see the cm/showCms effects above). */
  skipTooltip?: boolean;
}

function fillLayer(
  layer: L.LayerGroup,
  pin: MapPin,
  defaultRadius: number,
  onOpenRef: { current: (href: string) => void },
  handlers?: PinHandlers,
  options?: FillLayerOptions,
): void {
  const baseRadius = pin.radius ?? defaultRadius;
  const baseWeight = pin.weight ?? BASE_WEIGHT;
  const filled = pin.filled ?? true;
  const opacity = pin.dimmed ? DIMMED_PIN_OPACITY : (pin.opacity ?? 1);
  const marker = L.circleMarker([pin.lat, pin.lng], {
    radius: baseRadius,
    color: pin.ringColor ?? (filled ? '#FFFFFF' : pin.color),
    weight: baseWeight,
    fillColor: pin.color,
    fillOpacity: filled ? opacity : 0,
    opacity,
    className: 'map-marker-pin',
  });
  if (pin.tag !== undefined) {
    marker.bindTooltip(pin.tag, nameTagOptions(baseRadius));
    marker.on('tooltipopen', () => placeNameTag(marker));
    marker.on('add', () => marker.getElement()?.setAttribute('aria-label', pin.title));
  } else if (options?.skipTooltip) {
    marker.on('add', () => marker.getElement()?.setAttribute('aria-label', pin.title));
  } else {
    marker.bindTooltip(pin.title, { direction: 'top', offset: [0, -6] });
  }
  marker.on('mouseover', (e?: L.LeafletMouseEvent) => {
    marker.setRadius(baseRadius + HOVER_RADIUS_DELTA);
    marker.setStyle({ weight: pin.hoverWeight ?? HOVER_WEIGHT });
    if (handlers?.onHover && e?.containerPoint) {
      handlers.onHover(pin, { x: e.containerPoint.x, y: e.containerPoint.y });
    }
  });
  marker.on('mouseout', () => {
    marker.setRadius(baseRadius);
    marker.setStyle({ weight: baseWeight });
    handlers?.onHoverEnd?.();
  });
  marker.on('click', (e?: L.LeafletMouseEvent) => {
    if (handlers?.onClick) {
      const point = e?.containerPoint ? { x: e.containerPoint.x, y: e.containerPoint.y } : { x: 0, y: 0 };
      handlers.onClick(pin, point);
    } else if (pin.href) {
      onOpenRef.current(pin.href);
    }
  });
  marker.addTo(layer);
}
